// The people-picker core (ios/Sources/Views/PeoplePickerCore.swift): a search field, chips for the
// chosen — in the order they were chosen, read as a sentence — and candidate rows.
//
// Local first (docs/PERFORMANCE.md): every keystroke paints the people the store already knows
// that match, in the same frame; the server's answer (§19.1 — the team's members at rest, a
// search once something is typed, debounced 300 ms like the Apple sheet) merges in underneath.
// Both write every returned person into the store, so a name shown here is the record every
// other screen reads.
//
// Keyboard: ↑/↓ move the highlight, Enter picks the highlighted person (and clears the query, so
// the next name can be typed), Enter with nothing highlighted — or ⌘/Ctrl+Enter — opens the
// conversation, Backspace in an empty field drops the last chip.
import { useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import type { PeopleResult } from '../../../data/features/people';
import { PeoplePickerPresentation, type Row } from '../../../core/peoplePickerPresentation';
import { CallInvitePlan } from '../../../core/callInvitePlan';
import type { UserRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive } from '../../hooks';
import { EmptyState, InlineStatus, SearchInput } from '../../kit/Kit';

const DEBOUNCE_MS = 300;

function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** The worker's local match (`peopleLocal`), for rows left over from an older query. */
function matchesTerm(u: UserRecord, term: string): boolean {
  const needle = fold(term.replace(/^@/, ''));
  if (!needle) return true;
  const hay = fold(`${u.username}\n${u.firstName ?? ''} ${u.lastName ?? ''}\n${u.lastName ?? ''}\n${u.nickname ?? ''}`);
  return hay.includes(needle);
}

export interface PeoplePickerProps {
  teamId: string;
  meId: string;
  selected: string[];
  onSelectedChange(next: string[]): void;
  /** Everyone the sheet has ever shown, so a chosen person keeps their chip. */
  known: Map<string, UserRecord>;
  /** Enter with nothing to pick, or ⌘/Ctrl+Enter. */
  onSubmit(): void;
  /** A pick that hit the 7-others ceiling (the footer explains). */
  onCeiling(): void;
  /** People never offered (the call invite: everyone already in the call). */
  excludeIds?: ReadonlySet<string>;
  /** The personal space (yourself) is offered — true for "start a conversation", the default. */
  includeSelf?: boolean;
  /** Offer people only: no bot accounts, no deactivated accounts (the call invite). Off by default. */
  peopleOnly?: boolean;
}

export function PeoplePicker({ teamId, meId, selected, onSelectedChange, known, onSubmit, onCeiling, excludeIds, includeSelf = true, peopleOnly = false }: PeoplePickerProps) {
  const [query, setQuery] = useState('');
  const [local, setLocal] = useState<{ term: string; result: PeopleResult }>({ term: '', result: { users: [], statuses: {} } });
  const [remote, setRemote] = useState<{ term: string; result: PeopleResult } | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [retryTick, setRetryTick] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const generation = useRef(0);

  const term = query.trim();

  // Store first, every keystroke; the server after a pause (at rest: immediately).
  useEffect(() => {
    const mine = ++generation.current;
    void data.call('peopleLocal', term).then((r) => {
      if (mine === generation.current) setLocal({ term, result: r });
    });
    setFailed(false);
    const timer = setTimeout(
      () => {
        setLoading(true);
        void data.call('peopleSearch', teamId, term).then(
          (r) => {
            if (mine !== generation.current) return;
            setLoading(false);
            if ('error' in r) setFailed(true);
            else setRemote({ term, result: r });
          },
          () => {
            if (mine !== generation.current) return;
            setLoading(false);
            setFailed(true);
          },
        );
      },
      term ? DEBOUNCE_MS : 0,
    );
    return () => clearTimeout(timer);
  }, [teamId, term, retryTick]);

  // An answer for an OLDER query is not an answer for this one: until the fresh ones land (a busy
  // worker can take a while), the older people are shown only where they still match what is
  // typed — so Enter can never pick someone the field no longer describes.
  const remoteForTerm = remote && remote.term === term ? remote.result : null;
  const candidates = useMemo(() => {
    const byId = new Map<string, UserRecord>();
    const stillMatching = (u: UserRecord) => matchesTerm(u, term);
    for (const u of local.term === term ? local.result.users : local.result.users.filter(stillMatching)) byId.set(u.id, u);
    const server = remoteForTerm?.users ?? (remote ? remote.result.users.filter(stillMatching) : []);
    for (const u of server) byId.set(u.id, u);
    const all = [...byId.values()];
    if (peopleOnly) {
      const bots = new Set([...(local.result.botIds ?? []), ...(remote?.result.botIds ?? [])]);
      return CallInvitePlan.offered(all, excludeIds ?? new Set(), bots);
    }
    return all.filter((u) => !excludeIds?.has(u.id));
  }, [local, remote, remoteForTerm, term, excludeIds, peopleOnly]);

  useEffect(() => {
    for (const u of candidates) known.set(u.id, u);
  }, [candidates, known]);

  const ids = useMemo(() => candidates.map((u) => u.id), [candidates]);
  const { value: liveStatuses } = useLive(() => data.call('peopleStatuses', ids), ['userStatuses'], [ids]);
  // Merged, never replaced: a live re-read answering for the PREVIOUS id list must not blank the
  // dots the search results already carried.
  const statuses = { ...local.result.statuses, ...(remote?.result.statuses ?? {}), ...(liveStatuses ?? {}) };

  const rows = useMemo(() => PeoplePickerPresentation.rows({ candidates, currentUserId: meId, selected, includeSelf }), [candidates, meId, selected, includeSelf]);
  const chips = PeoplePickerPresentation.chips(selected, known);
  const rowsKey = rows.map((r) => r.userId).join(',');

  // Typing highlights the first person who can be picked; clearing the field drops the highlight
  // so Enter opens the conversation.
  useEffect(() => {
    if (!term) {
      setHighlight(-1);
      return;
    }
    setHighlight(rows.findIndex((r) => PeoplePickerPresentation.isSelectable(r, selected, meId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, rowsKey]);

  useEffect(() => {
    if (highlight < 0) return;
    listRef.current?.querySelector(`[data-index="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const toggle = (row: Row) => {
    if (!PeoplePickerPresentation.isSelectable(row, selected, meId)) {
      if (!row.isDeactivated && !row.isSelected && !PeoplePickerPresentation.canSelectMore(selected.length)) onCeiling();
      return false;
    }
    onSelectedChange(PeoplePickerPresentation.toggle(selected, row.userId));
    return true;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h < 0 ? (step > 0 ? 0 : rows.length - 1) : (h + step + rows.length) % rows.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) {
        onSubmit();
        return;
      }
      const row = highlight >= 0 ? rows[highlight] : null;
      if (row) {
        if (toggle(row) && term) setQuery('');
      } else if (selected.length) onSubmit();
    } else if (e.key === 'Backspace' && query === '' && selected.length) {
      onSelectedChange(selected.slice(0, -1));
    }
  };

  // "No one matches" only once the SERVER said so for this very query; until then, "Buscando…".
  const busyFirstPaint = !rows.length && !failed && remoteForTerm === null && (loading || term !== '');

  return (
    <>
      <div className="people-sticky" onKeyDown={onKeyDown}>
        <SearchInput value={query} onChange={setQuery} placeholder={t('people.search')} autoFocus />
        {chips.length ? (
          <div className="people-chips">
            {chips.map((chip) => (
              <button
                key={chip.userId}
                className="people-chip"
                onClick={() => onSelectedChange(PeoplePickerPresentation.toggle(selected, chip.userId))}
                aria-label={tf('web.people.removeChip', chip.name)}
                title={tf('web.people.removeChip', chip.name)}
              >
                <Avatar userId={chip.userId} name={chip.name} size={22} version={chip.lastPictureUpdate} />
                <span className="people-chip-name">{chip.userId === meId ? tf('people.yourself', chip.name) : chip.name}</span>
                <Icon name="x" size={13} />
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {failed && !rows.length ? (
        <InlineStatus kind="error" text={t('people.searchFailed')} onRetry={() => setRetryTick((n) => n + 1)} />
      ) : null}
      {busyFirstPaint ? <InlineStatus kind="loading" text={t('web.people.searching')} /> : null}
      {!rows.length && remoteForTerm !== null && term ? <EmptyState title={tf('web.people.noMatches', term)} /> : null}
      <div className="people-list" ref={listRef} role="listbox" aria-multiselectable="true" aria-label={t('people.search')}>
        {rows.map((row, index) => {
          const selectable = PeoplePickerPresentation.isSelectable(row, selected, meId);
          const presence = statuses[row.userId] ?? null;
          return (
            <button
              key={row.userId}
              data-index={index}
              role="option"
              aria-selected={row.isSelected}
              aria-disabled={!selectable}
              className={`list-row clickable people-row${row.isSelected ? ' selected' : ''}${selectable ? '' : ' unavailable'}${index === highlight ? ' highlighted' : ''}`}
              onMouseMove={() => index !== highlight && setHighlight(index)}
              onClick={() => toggle(row)}
            >
              <span className="list-row-leading">
                <Avatar userId={row.userId} name={row.name} size={32} version={row.lastPictureUpdate} presence={row.isDeactivated ? null : presence} />
              </span>
              <span className="list-row-text">
                <span className="list-row-title">
                  {row.isSelf ? tf('people.yourself', row.name) : row.name}
                  {row.isDeactivated ? <span className="people-badge">{t('web.people.profile.deactivatedBadge')}</span> : null}
                </span>
                <span className="list-row-subtitle people-handle">@{row.username}</span>
              </span>
              <span className="list-row-trailing">
                <span className="people-check" aria-hidden="true">
                  {row.isSelected ? <Icon name="check" size={13} strokeWidth={2.4} /> : null}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {failed && rows.length ? <InlineStatus kind="error" text={t('people.searchFailed')} onRetry={() => setRetryTick((n) => n + 1)} /> : null}
    </>
  );
}
